//@ts-ignore-next-line
import { createVuetify, type ThemeDefinition } from 'vuetify';
import { md3 } from 'vuetify/blueprints';
import { VAlert, VBtn, VSelect, VTextField } from 'vuetify/components';

import vuetifyLocalization from '@/helpers/constants/vuetifyLocalization.json';

import 'vuetify/styles';
const myCustomLightTheme: ThemeDefinition = {
  dark: false,
  colors: {
    tertiary: '#4a607b',
    error: '#FF3B30',
    danger: '#ba1a1a',

    gray: '#7d7d7d',
    light: '#f3f3f3',
    hint: '#ececec',
    'table-bg': '#F9FAFC',
    'chip-bg': '#FEF7FF',
    'chip-text': '#49454F',
    'gray-100': '#00000033',
    'black-400': '#8F8F8F',
    'black-700': '#3B3B3B',
    'black-800': '#1C1C1C',
    'black-900': '#010101',
    'black-600': '#575757',
    'black-500': '#737373',

    'blue-700': '#004299',
    'blue-300': '#05DBF2',
    'blue-50': '#E5F1FF',
    'blue-75': '#BDDCF6',
    'blue-1': '#0583F2',
    'blue-2': '#004396',
    'blue-3': '#05DBF2',
    main: '#1F2F49',
    green: '#22883C',
    white: '#ffffff',
    black: '#000000',
    background: '#ffffff',
    outline: '#6f7978',
    'dark-gray': '#323639',
    'light-pink': '#F5EDFF',
    primary: '#00416b',
    header: '#00416b',
    success: '#22883C',
    'gray-1': '#707070',
    info: '#54c0e8',
    'dark-text': '#404040',
    blue: '#75ADDF',
    text: '#666666',
    'black-200': '#C7C7C7',
    'blue-800': '#002C66',
    'black-300': '#ABABAB',
    'blue-text': '#465465',
    linkedin: '#0A66C2',
    facebook: '#0866FF',
    yellow: '#F5B335',
    themeIcon: '#E5F1FF',
    themeBtn: '#002C66',
    tabbyWidget: '#FFFFFF',
    hoverReviewCard: '#F4F4F4',
    reviewCard: '#FCFCFC',
  },
  variables: {
    'high-emphasis-opacity': 0.87, // default 0.87
    'medium-emphasis-opacity': 0.6, // default 0.6
    'disabled-opacity': 0.38, // default 0.38
    'idle-opacity': 0.04, // default 0.04
    'hover-opacity': 0.04, // default 0.04
    'focus-opacity': 0.12, // default 0.12
    'selected-opacity': 0.08, // default 0.08
    'activated-opacity': 0.12, // default 0.12
    'pressed-opacity': 0.12, // default 0.12
    'dragged-opacity': 0.08, // default 0.08
  },
};

//TODO someone fix this pls, on refresh the header and background are messed up
const myCustomDarkTheme: ThemeDefinition = {
  dark: true,
  colors: {
    tertiary: '#7B9BC4',
    error: '#FF453A',
    danger: '#FF5449',
    gray: '#A8A8A8',
    light: '#444444ff',
    themeIcon: '#002C66',
    themeBtn: '#E5F1FF',
    tabbyWidget: '#E5E1EC',
    hint: '#2A2A2A',
    'table-bg': '#1A1D21',
    'chip-bg': '#2D1B35',
    'chip-text': '#E5E1EC',
    'gray-100': '#FFFFFF1A',
    'black-400': '#B8B8B8',
    'black-700': '#CCCCCC',
    'black-800': '#E3E3E3',
    'black-900': '#FEFEFE',
    'black-600': '#A8A8A8',
    'blue-700': '#5C9AFF',
    'blue-300': '#4ECDE0',
    'blue-50': '#262e36ff', // Your E5F1FF converted to dark
    'blue-75': '#2A3B4A',
    'blue-1': '#3A96FF',
    'blue-2': '#5C9AFF',
    'blue-3': '#4ECDE0',
    main: '#E5E5E5',
    green: '#32CD32',
    white: '#1f1f1f',
    black: '#FFFFFF',
    background: '#0F0F0F',
    outline: '#90A4A3',
    'dark-gray': '#D9DCDF',
    'light-pink': '#2D1F35',
    primary: '#4A90D9',
    header: '#4A90D9',
    success: '#32CD32',
    'gray-1': '#B0B0B0',
    info: '#6BD1F5',
    'dark-text': '#E0E0E0',
    blue: '#8AC7FF',
    text: '#CCCCCC',
    'black-200': '#585858',
    'blue-800': '#7AA3FF',
    'black-300': '#747474',
    'blue-text': '#B4C4D4',
    linkedin: '#4A9EFF',
    facebook: '#4A8AFF',
    yellow: '#FFD700',
  },
  variables: {
    'high-emphasis-opacity': 0.87, // default 0.87
    'medium-emphasis-opacity': 0.6, // default 0.6
    'disabled-opacity': 0.38, // default 0.38
    'idle-opacity': 0.04, // default 0.04
    'hover-opacity': 0.04, // default 0.04
    'focus-opacity': 0.12, // default 0.12
    'selected-opacity': 0.08, // default 0.08
    'activated-opacity': 0.12, // default 0.12
    'pressed-opacity': 0.12, // default 0.12
    'dragged-opacity': 0.08, // default 0.08
  },
};
const savedTheme = localStorage.getItem('theme');

const vuetify = createVuetify({
  // https://vuetifyjs.com/en/features/treeshaking/
  blueprint: md3,
  theme: {
    defaultTheme:
      import.meta.env.VITE_HIDE_DARK_THEME === 'true'
        ? 'myCustomLightTheme'
        : savedTheme || 'myCustomLightTheme',
    // defaultTheme: 'myCustomLightTheme',
    themes: {
      myCustomLightTheme,
      myCustomDarkTheme,
    },
    variations: {
      colors: ['primary', 'secondary'],
      lighten: 6,
      darken: 2,
    },
  },
  locale: {
    locale: 'en',
    fallback: 'en',
    messages: vuetifyLocalization,
  },
  aliases: {
    // use vuetify components as baseline for your custom component
    VCustomTextField: VTextField,
    VCustomBtn: VBtn,
    VCancelBtn: VBtn,
    VError: VAlert,
    VOutlineField: VTextField,
    VOutlineSelect: VSelect,
    VBtnText: VBtn,
  },

  defaults: {
    VBtn: {
      class: 'text-capitalize',
      rounded: 'xs',
      color: '',
    },
    // set default prop values for vuetify components
    VCustomBtn: {
      color: 'primary',
      class: 'text-capitalize px-4 py-3 h-auto w-auto',
      size: 'default',
      'min-width': '200px',
      rounded: 'xs',
    },
    VCancelBtn: {
      variant: 'text',
      class: 'text-capitalize px-4 py-3 h-auto',
      size: 'default',
      rounded: 'xs',
      color: '',
    },
    VAutocomplete: {
      variant: 'outlined',
      density: 'default',
    },
    VTab: {
      class: 'text-capitalize',
    },
    VBtnText: {
      variant: 'text',
      class: 'text-capitalize text-subtitle-2',
      color: 'primary',
      rounded: 0,
      height: '35px',
    },
    VOutlineField: {
      variant: 'outlined',
      density: 'compact',
      class: '  rounded',
    },
    VOutlineSelect: {
      variant: 'outlined',
      density: 'compact',
      class: '  rounded',
    },
    VTextField: {
      density: 'comfortable',
    },
  },
});

export default vuetify;
